import { Controller } from "@hotwired/stimulus"

/**
 * Theme Controller
 *
 * Manages light/dark mode with system preference detection.
 * Defaults to system preference, stores user choice in localStorage.
 *
 * Usage:
 *   <html data-controller="theme">
 *     <button data-action="click->theme#toggle">Toggle theme</button>
 *   </html>
 */
export default class extends Controller {
  static targets = ["icon"]

  // Storage key for persistence
  static STORAGE_KEY = "ablefy-theme-preference"

  connect() {
    // Load saved preference or use system default
    const saved = localStorage.getItem(this.constructor.STORAGE_KEY)

    if (saved) {
      // User has a saved preference
      this.currentTheme = saved
    } else {
      // No saved preference - use system default
      this.currentTheme = this.systemPrefersDark ? "dark" : "light"
    }

    // Apply theme immediately
    this.applyTheme()

    // Listen for system preference changes (only affects if no saved preference)
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.boundHandleSystemChange = this.handleSystemChange.bind(this)
    this.mediaQuery.addEventListener("change", this.boundHandleSystemChange)
  }

  disconnect() {
    if (this.mediaQuery && this.boundHandleSystemChange) {
      this.mediaQuery.removeEventListener("change", this.boundHandleSystemChange)
    }
  }

  /**
   * Toggle between light and dark
   */
  toggle() {
    this.currentTheme = this.currentTheme === "dark" ? "light" : "dark"
    this.savePreference()
    this.applyTheme()
  }

  /**
   * Handle system preference change
   * Only applies if user hasn't set a preference
   */
  handleSystemChange(e) {
    const saved = localStorage.getItem(this.constructor.STORAGE_KEY)
    if (!saved) {
      this.currentTheme = e.matches ? "dark" : "light"
      this.applyTheme()
    }
  }

  /**
   * Apply the current theme to the document
   */
  applyTheme() {
    document.documentElement.setAttribute("data-theme", this.currentTheme)
    this.updateUI()
  }

  /**
   * Check if system prefers dark mode
   */
  get systemPrefersDark() {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
  }

  /**
   * Save preference to localStorage
   */
  savePreference() {
    localStorage.setItem(this.constructor.STORAGE_KEY, this.currentTheme)
  }

  /**
   * Update UI elements (icons)
   */
  updateUI() {
    if (this.hasIconTarget) {
      this.iconTargets.forEach(icon => {
        const showFor = icon.dataset.themeIcon
        // Show sun when dark (to switch to light), show moon when light (to switch to dark)
        icon.classList.toggle("hidden", showFor !== this.currentTheme)
      })
    }
  }
}
